import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articleList } from '@/mocks/articles';
import { categoryList } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categoryList.find((c) => c.slug === slug);
  const articles = articleList.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">分类不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://91yingku.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: 'http://91yingku.cn/',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/60">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-black text-foreground-950 font-heading leading-tight mb-3">
                  {category.name}专区
                </h1>
                <p className="text-sm text-foreground-500 mb-6">{category.description}</p>
                <div className="grid grid-cols-3 gap-4 py-4 border-t border-background-200/60">
                  <div>
                    <p className="text-2xl md:text-3xl font-black text-secondary-500">
                      {category.count.toLocaleString()}+
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">爆料文章数</p>
                  </div>
                  <div>
                    <p className="text-2xl md:text-3xl font-black text-secondary-500">
                      {category.videoCount.toLocaleString()}+
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">翻车视频数</p>
                  </div>
                  <div>
                    <p className="text-2xl md:text-3xl font-black text-secondary-500">
                      {category.todayUpdate}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">今日更新</p>
                  </div>
                </div>
              </div>

              <div className="lg:w-[380px] shrink-0 bg-gradient-to-br from-secondary-100/60 to-secondary-50/40 rounded-2xl p-8 flex items-center justify-center min-h-[200px] relative overflow-hidden">
                <div className="absolute top-4 right-6 w-16 h-16 rounded-2xl bg-secondary-300/40 rotate-12"></div>
                <div className="absolute bottom-6 left-4 w-20 h-20 rounded-full bg-secondary-200/30"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-3xl bg-secondary-300/20 -rotate-45"></div>
                <i className={`${category.icon} text-5xl text-secondary-500/60 relative z-10`}></i>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 bg-background-50 pb-6">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-wrap gap-2">
              {categoryList.map((cat) => (
                <Link
                  key={cat.slug}
                  to="/"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {articles.map((article) => (
                <ArticleCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  excerpt={article.excerpt}
                  categoryName={article.categoryName}
                  publishedAt={article.publishedAt}
                  cardImageUrl={article.cardImageUrl}
                  views={article.views}
                  comments={article.comments}
                  isHot={article.isHot}
                  isExplosive={article.isExplosive}
                  variant="default"
                />
              ))}
            </div>
            {articles.length === 0 && (
              <p className="text-center text-foreground-500 py-20">暂无该分类的文章，敬请期待。</p>
            )}
          </div>
        </section>

        {articles.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="bg-foreground-950 rounded-2xl p-6 md:p-10 flex flex-col md:flex-row gap-6 md:gap-8 overflow-hidden relative">
                <div className="absolute top-0 right-10 w-32 h-32 rounded-full bg-secondary-500/10 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-10 w-24 h-24 rounded-2xl bg-accent-500/10 translate-y-1/2 rotate-45"></div>

                <div className="md:w-1/3 shrink-0 relative z-10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20news%20reporter%20portrait%20with%20notebook%2C%20investigative%20journalism%20style%2C%20dark%20background%2C%20dramatic%20lighting%2C%20editorial%20portrait%20photography%2C%20warm%20tone%20highlights&width=400&height=500&seq=category-testimonial&orientation=portrait"
                    alt="吃瓜爆料网记者"
                    title="吃瓜爆料网 - 一线记者"
                    className="w-full aspect-[4/5] object-cover object-top rounded-xl"
                    loading="lazy"
                  />
                </div>

                <div className="md:w-2/3 flex flex-col justify-center relative z-10">
                  <span className="text-5xl text-secondary-400 font-serif leading-none mb-4">&ldquo;</span>
                  <p className="text-base md:text-lg text-background-50 leading-relaxed mb-4">
                    在这个信息爆炸的时代，我们坚持只做一件事——为你筛选最劲爆、最真实、最值得吃的瓜。每一篇爆料背后都有我们团队的多方核实和深入调查，确保你吃到的每一口瓜都新鲜保真。
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-background-50">吃瓜编辑部</span>
                    <span className="text-xs text-background-50/50">· 主笔</span>
                  </div>
                  <Link
                    to="/about"
                    className="mt-6 px-5 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 font-medium rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2 w-fit"
                  >
                    了解我们 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}